<template>
  <v-card class="my-4">
    <v-card-text>
      <v-form ref="form" v-model="valid" lazy-validation>
        <div
          v-if="unUsedMedicalAlerts && unUsedMedicalAlerts.length > 0"
          class="d-flex flex-column"
        >
          <v-select
            dense
            outlined
            label="Medical Alert"
            v-model="selectedMedicalAlert"
            :items="unUsedMedicalAlerts"
            item-value="id"
            item-text="name"
            return-object
            clearable
            :rules="[rules.required]"
            @change="medicalChanged"
            :hint="
              selectedMedicalAlert ? selectedMedicalAlert.description : null
            "
          />
          <v-select
            class="my-2"
            v-if="selectedMedicalAlert && selectedMedicalAlert.withOptions"
            dense
            outlined
            multiple
            :rules="[rules.required]"
            :label="`${selectedMedicalAlert.name} Options`"
            v-model="selectedMedicalAlertOptions"
            :items="medicalAlertOptions"
            item-value="id"
            item-text="name"
            return-object
            clearable
          />

          <table
            v-if="
              selectedMedicalAlertOptions &&
              selectedMedicalAlertOptions.length > 0
            "
          >
            <tr
              v-for="(option, index) in selectedMedicalAlertOptions"
              :key="option.id"
            >
              <td>{{ index + 1 }}.</td>
              <td>
                <v-textarea
                  class="my-2"
                  v-model="option.memo"
                  :auto-grow="true"
                  :rows="1"
                  :label="`${selectedMedicalAlert.name} - ${option.name} Memo`"
                  outlined
                  hide-details
                  dense
                />
              </td>
            </tr>
          </table>
          <div v-else>
            <v-textarea
              v-if="selectedMedicalAlert && !selectedMedicalAlert.withOptions"
              class="my-2"
              v-model="selectedMedicalAlert.memo"
              :auto-grow="true"
              :rows="1"
              :label="`${selectedMedicalAlert.name} Memo`"
              outlined
              hide-details
              dense
            />
          </div>

          <div class="d-flex justify-center ma-6">
            <v-btn @click="addMedicalAlert" color="primary" class="ml-2">
              Add
            </v-btn>
          </div>
        </div>

        <v-divider />

        <div
          v-if="!studentMedicals || studentMedicals.length < 1"
          class="text-center text-caption ma-6"
        >
          No medical alert assigned for {{ student.firstName }}
          {{ student.lastName }}
        </div>
        <div class="my-4" v-else>
          <div class="text-subtitle-1 text-center ma-4">
            {{ `${student.firstName} ${student.lastName}'s Medical Alert` }}
          </div>
          <v-row>
            <v-col
              cols="12"
              md="6"
              v-for="studentMedical in studentMedicals"
              :key="studentMedical.id"
            >
              <v-sheet :elevation="2" class="pa-4">
                <div class="d-flex justify-end">
                  <v-btn icon @click="deleteStudentMedical(studentMedical)">
                    <v-icon> mdi-delete </v-icon>
                  </v-btn>
                </div>
                <div class="text-center text-h6">
                  {{ studentMedical.medicalAlertName }}
                  <span v-if="studentMedical.medicalAlertOptionId">
                    - {{ studentMedical.medicalAlertOptionName }}
                  </span>
                  <div class="text-subtitle-2 ma-2" v-if="studentMedical.memo">
                    Memo: {{ studentMedical.memo }}
                  </div>
                </div>
                <div class="text-caption text-right mt-4">
                  Recorded on {{studentMedical.createdAt | formatDateYear}}
                </div>
              </v-sheet>
            </v-col>
          </v-row>
        </div>
      </v-form>
    </v-card-text>
    <confirmation-dialog ref="confirm" />
  </v-card>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { rules } from "@/store/util/rules.js";
import Axios from "axios";
const ConfirmationDialog = () =>
  import("@/components/common/ConfirmationDialog.vue");
import { fetchStudentMedicalAlerts } from "@/services/medicalAlertServices";

export default {
  props: {
    student: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    studentMedicals: [],
    medicalAlerts: [],
    medicalAlertOptions: [],
    selectedMedicalAlertOptions: [],
    selectedMedicalAlert: null,
    rules,
  }),
  components: { ConfirmationDialog },
  async mounted() {
    this.studentMedicals = await fetchStudentMedicalAlerts(this.student.id);

    await this.getMedicalAlerts();

    this.medicalChanged();
  },
  computed: {
    ...mapGetters("auth", ["isLoggedIn", "currentUser", "userRoles"]),
    unUsedMedicalAlerts() {
      if (this.medicalAlerts && this.medicalAlerts.length > 0) {
        if (this.studentMedicals && this.studentMedicals.length > 0) {
          const usedMedicalAlertIds = this.studentMedicals.map(
            (sm) => sm.medicalAlertId
          );
          return this.medicalAlerts.filter(
            (m) => m.withOptions || !usedMedicalAlertIds.includes(m.id)
          );
        } else {
          return this.medicalAlerts;
        }
      } else {
        return [];
      }
    },
  },
  methods: {
    ...mapActions("messages", [
      "addErrorMessage",
      "addSuccessMessage",
      "clearMessage",
    ]),

    ...mapActions("medicalAlert", ["fetchMedicalAlerts"]),
    getMedicalAlerts() {
      //default to sorting by ID desc if not defined
      const sortBy = "name";
      const sortDesc = false;
      const params = {
        page: 0,
        size: 1000,
        sort: `${sortBy}${sortDesc ? ",desc" : ""}`,
      };
      this.fetchMedicalAlerts(params)
        .then((response) => {
          if (response) {
            this.medicalAlerts = response.content;
          }
        })
        .catch((error) => {
          this.addErrorMessage("Server response with error " + error);
        });
    },
    cancel() {
      this.$emit("cancel");
    },
    addMedicalAlert() {
      if (!this.$refs.form.validate()) {
        return;
      }

      const data = {
        studentId: this.student.id,
        medicalAlertId: this.selectedMedicalAlert
          ? this.selectedMedicalAlert.id
          : null,
      };

      const requests = [];
      if (
        this.selectedMedicalAlertOptions &&
        this.selectedMedicalAlertOptions.length > 0
      ) {
        this.selectedMedicalAlertOptions.forEach((option) => {
          const reqData = Object.assign({}, data);
          reqData.medicalAlertOptionId = option.id;
          reqData.memo = option.memo;
          const request = this.$axios.post("/studentMedicalAlert", reqData);
          requests.push(request);
        });
      } else {
        data.memo = this.selectedMedicalAlert.memo;
        const request = this.$axios.post("/studentMedicalAlert", data);
        this.$emit("saved");

        requests.push(request);
      }
      Axios.all(requests).then(
        Axios.spread((...responses) => {
          if (responses) {
            responses.forEach((response) => {
              const studentMedical = response.data;
              this.studentMedicals.push(studentMedical);
            });

            this.selectedMedicalAlert = null;
            this.selectedMedicalAlertOptions = null;
            this.medicalChanged();
          }
        })
      );
    },
    medicalChanged() {
      this.medicalAlertOptions = this.selectedMedicalAlert ? this.selectedMedicalAlert.options:[];
      this.selectedMedicalAlertOptions = null;
      if (this.medicalAlertOptions) {
        //filter used options
        let usedMedicalAlertOptionIds = [];
        if (this.studentMedicals && this.studentMedicals.length > 0) {
          usedMedicalAlertOptionIds = this.studentMedicals.map(
            (sm) => sm.medicalAlertOptionId
          );
        }
        this.medicalAlertOptions = this.medicalAlertOptions.filter(
          (o) => !usedMedicalAlertOptionIds.includes(o.id)
        );
        this.medicalAlertOptions.sort((a, b) => a.name.localeCompare(b.name));
      }

      this.$emit('change')
    },
    async deleteStudentMedical(studentMedical) {
      let confirm = await this.$refs.confirm.open(
        "Confirm Delete",
        "Do you want to delete this student's medical alert?"
      );

      if (confirm) {
        this.$axios
          .delete("/studentMedicalAlert/" + studentMedical.id)
          .then(() => {
            this.studentMedicals.splice(
              this.studentMedicals.findIndex(
                (sm) => sm.id == studentMedical.id
              ),
              1
            );
            this.medicalChanged();
            this.addSuccessMessage("Student medical deleted successfully");
          });
      }
    },
  },
};
</script>

<style>
</style>